import React, { useState } from 'react'
import { IconFolderPlus, IconSearch } from '@tabler/icons-react'
import { Input } from '../../atoms/inputs/Input'
import Loader from '../../atoms/Loader'
import Typography from '../../atoms/Typography'
import FolderButton from '../../atoms/FolderButton'
import FolderTreeNode from '../../molecules/FolderTreeNode'
import { DataRoomFolder } from '../../../types/projects'
import CreateFolderModal from './CreateFolderModal'
import { PrimaryButton } from '../../atoms/buttons/PrimaryButton'

interface DataRoomSidebarProps {
  folders: DataRoomFolder[]
  totalDocuments: number
  isLoading: boolean
  selectedFolderPath: string | null
  onSelectFolder: (path: string | null) => void
  searchTerm: string
  onSearchChange: (value: string) => void
  projectId: string
  onRenameFolder: (folder: DataRoomFolder, newName: string) => Promise<void>
  onDeleteFolder: (folder: DataRoomFolder) => void
  actionsEnabled: boolean
}

const DataRoomSidebar: React.FC<DataRoomSidebarProps> = ({
  folders,
  totalDocuments,
  isLoading: isFoldersLoading,
  selectedFolderPath,
  onSelectFolder,
  searchTerm,
  onSearchChange,
  projectId,
  onRenameFolder,
  onDeleteFolder,
  actionsEnabled,
}) => {
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false)

  return (
    <div className="flex w-[288px] shrink-0 flex-col pt-8">
      <div>
        <Input
          icon={<IconSearch className="h-4 w-4 text-light-green-500" />}
          className="h-8 border-gray-700 bg-gray-800 py-1 font-inter text-sm font-normal text-gray-50 placeholder:text-gray-400"
          placeholder="Search documents..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>

      <div className="mt-8 flex-1 overflow-y-auto">
        {isFoldersLoading ? (
          <div className="flex h-full items-center justify-center">
            <Loader className="text-light-green-500" />
          </div>
        ) : (
          <>
            <div className="flex items-center">
              {/* Matches the tree nodes' chevron slot so every row's folder icon lines up */}
              <div className="w-5 shrink-0" />
              <div className="min-w-0 flex-1">
                <FolderButton
                  label="All Files"
                  count={totalDocuments}
                  isSelected={selectedFolderPath === null}
                  onClick={() => onSelectFolder(null)}
                />
              </div>
            </div>
            {folders.map((folder) => (
              <FolderTreeNode
                key={folder.id}
                projectId={projectId}
                folder={folder}
                depth={0}
                selectedFolderPath={selectedFolderPath}
                onSelectFolder={onSelectFolder}
                onRenameFolder={onRenameFolder}
                onDeleteFolder={onDeleteFolder}
                actionsEnabled={actionsEnabled}
              />
            ))}
          </>
        )}
      </div>

      <PrimaryButton
        className="my-4"
        size="sm"
        leftIcon={<IconFolderPlus className="h-4 w-4" />}
        onClick={() => setIsCreateFolderOpen(true)}
      >
        {selectedFolderPath ? 'Create Subfolder' : 'Create Folder'}
      </PrimaryButton>

      <div className="mb-8 rounded-lg border border-gray-500 bg-gray-800 p-4">
        <div className="mb-2 flex items-center gap-2">
          <Typography variant="xs" className="font-medium text-gray-50">
            💡 About Data Room
          </Typography>
        </div>
        <Typography variant="xs" element="p" className="text-gray-200">
          Documents here enhance Copa&apos;s understanding of your project and serve as a centralized repository for
          stakeholders.
        </Typography>
      </div>

      <CreateFolderModal
        isOpen={isCreateFolderOpen}
        onClose={() => setIsCreateFolderOpen(false)}
        projectId={projectId}
        parentPath={selectedFolderPath}
      />
    </div>
  )
}

export default DataRoomSidebar
